/* All amounts are USD. Percent inputs use 0–100, never decimal fractions. */
const field = (key, label, value, percent = false) => ({
  key,
  label,
  value,
  percent,
  integer: ['customers', 'creators', 'partners', 'visits'].includes(key)
});
const calculatorDefinitions = {
  commission: {
    fields: [
      field('revenue', 'Eligible sales (USD)', 10000),
      field('rate', 'Commission (%)', 15, true)
    ],
    formula:
      'Commission = eligible sales × commission rate. Eligible sales exclude refunds, taxes and shipping.',
    calculate: (v) => [
      ['Partner commission', (v.revenue * v.rate) / 100],
      ['Sales after commission', v.revenue * (1 - v.rate / 100)]
    ]
  },
  profit: {
    fields: [
      field('revenue', 'Net sales (USD)', 10000),
      field('goods', 'Product costs (USD)', 3500),
      field('rate', 'Commission (%)', 15, true),
      field('other', 'Other variable costs (USD)', 1000)
    ],
    formula:
      'Contribution = net sales − product costs − commission − other variable costs. Fixed overhead and income tax are excluded.',
    calculate: (v) => [
      [
        'Contribution after commission',
        v.revenue - v.goods - (v.revenue * v.rate) / 100 - v.other
      ],
      ['Commission expense', (v.revenue * v.rate) / 100]
    ]
  },
  breakEven: {
    fields: [
      field('fixed', 'Campaign fixed cost (USD)', 1000),
      field('aov', 'Net order value (USD)', 80),
      field('cost', 'Variable cost per order (USD)', 35),
      field('rate', 'Commission (%)', 15, true)
    ],
    formula:
      'Orders to break even = fixed cost ÷ (net order value − variable cost − commission), rounded up. Variable cost must include fulfillment and payment fees. With no fixed cost, zero orders break even; a negative contribution still means every additional order loses money.',
    calculate: (v) => {
      const margin = v.aov - v.cost - (v.aov * v.rate) / 100;
      return [
        ['Contribution per order', margin],
        [
          'Orders to break even',
          v.fixed === 0
            ? 0
            : margin > 0
            ? Math.ceil(v.fixed / margin)
            : 'Not attainable with these unit economics',
          'count'
        ]
      ];
    }
  },
  discount: {
    fields: [
      field('price', 'Original price (USD)', 100),
      field('discount', 'Customer discount (%)', 10, true),
      field('cost', 'Product and fulfillment cost (USD)', 35),
      field('rate', 'Commission on discounted price (%)', 15, true)
    ],
    formula:
      'Contribution = price × (1 − discount) × (1 − commission) − product and fulfillment cost. Other expenses are excluded.',
    calculate: (v) => {
      const net = v.price * (1 - v.discount / 100);
      return [
        ['Customer price', net],
        ['Partner commission', (net * v.rate) / 100],
        ['Contribution', net * (1 - v.rate / 100) - v.cost]
      ];
    }
  },
  roi: {
    fields: [
      field('revenue', 'Campaign net sales (USD)', 15000),
      field('goods', 'Product and fulfillment cost (USD)', 6000),
      field('spend', 'Total campaign investment (USD)', 3000)
    ],
    formula:
      'ROI = (net sales − product and fulfillment costs − campaign investment) ÷ campaign investment × 100. Investment includes commissions, fees and gifts; do not count those again in product costs.',
    calculate: (v) => [
      ['Campaign profit', v.revenue - v.goods - v.spend],
      [
        'Return on investment',
        v.spend > 0
          ? ((v.revenue - v.goods - v.spend) / v.spend) * 100
          : 'Enter investment above zero',
        'percent'
      ]
    ]
  },
  cac: {
    fields: [
      field('spend', 'Acquisition spend (USD)', 2500),
      field('customers', 'New customers', 100)
    ],
    formula:
      'CAC = acquisition spend ÷ new customers. Count only first-time customers, and include all acquisition costs.',
    calculate: (v) => [
      [
        'Customer acquisition cost',
        v.customers > 0
          ? v.spend / v.customers
          : 'Enter at least one new customer'
      ]
    ]
  },
  forecast: {
    fields: [
      field('partners', 'Active partners', 20),
      field('visits', 'Monthly visits per partner', 200),
      field('conversion', 'Visit-to-order conversion (%)', 2, true),
      field('aov', 'Net order value (USD)', 75)
    ],
    formula:
      'Expected orders = active partners × visits per partner × conversion. Revenue = expected orders × net order value. This is a scenario, not a prediction; fractional orders represent an average.',
    calculate: (v) => {
      const orders = (v.partners * v.visits * v.conversion) / 100;
      return [
        ['Expected monthly orders', orders, 'count'],
        ['Expected monthly revenue', orders * v.aov]
      ];
    }
  },
  gifting: {
    fields: [
      field('creators', 'Creators receiving gifts', 25),
      field('product', 'Product cost per gift (USD)', 20),
      field('shipping', 'Shipping per gift (USD)', 8),
      field('posting', 'Expected posting rate (%)', 60, true)
    ],
    formula:
      'Gift budget = creators × (product cost + shipping). Cost per expected post = gift budget ÷ (creators × posting rate). Gifts do not guarantee coverage.',
    calculate: (v) => {
      const cost = v.creators * (v.product + v.shipping),
        posts = (v.creators * v.posting) / 100;
      return [
        ['Gift budget', cost],
        ['Expected posts', posts, 'count'],
        [
          'Cost per expected post',
          posts > 0 ? cost / posts : 'No posts expected in this scenario'
        ]
      ];
    }
  },
  payout: {
    fields: [
      field('sales', 'Gross attributed sales (USD)', 5000),
      field('refunds', 'Refunded eligible sales (USD)', 400),
      field('rate', 'Commission (%)', 15, true),
      field('paid', 'Commission already paid (USD)', 200)
    ],
    formula:
      'Balance = (gross attributed sales − refunded eligible sales) × commission − already paid. A negative balance indicates overpayment to review; refunds cannot exceed sales.',
    validate: (v) =>
      v.refunds > v.sales ? 'Refunded sales cannot exceed gross sales.' : null,
    calculate: (v) => [
      ['Earned commission', ((v.sales - v.refunds) * v.rate) / 100],
      ['Outstanding balance', ((v.sales - v.refunds) * v.rate) / 100 - v.paid]
    ]
  },
  tiers: {
    fields: [
      field('sales', 'Eligible sales (USD)', 12000),
      field('threshold', 'Sales threshold (USD)', 10000),
      field('base', 'Base commission (%)', 10, true),
      field('bonus', 'Commission above threshold (%)', 15, true)
    ],
    formula:
      'Progressive commission = sales up to threshold × base rate + sales above threshold × higher rate. The higher rate applies only to the portion above the threshold.',
    calculate: (v) => [
      [
        'Total commission',
        (Math.min(v.sales, v.threshold) * v.base) / 100 +
          (Math.max(0, v.sales - v.threshold) * v.bonus) / 100
      ],
      ['Sales in higher tier', Math.max(0, v.sales - v.threshold)]
    ]
  },
  refunds: {
    fields: [
      field('sales', 'Attributed sales before refunds (USD)', 10000),
      field('refundRate', 'Expected refunded share of sales (%)', 8, true),
      field('rate', 'Commission (%)', 15, true)
    ],
    formula:
      'Commission reserve = attributed sales × refunded share × commission rate. This models full commission reversals on refunded eligible sales, not a payout policy.',
    calculate: (v) => [
      [
        'Estimated commission reserve',
        (((v.sales * v.refundRate) / 100) * v.rate) / 100
      ],
      [
        'Estimated commission after refunds',
        (v.sales * (1 - v.refundRate / 100) * v.rate) / 100
      ]
    ]
  },
  conversion: {
    fields: [
      field('visits', 'Qualified visits', 5000),
      field('current', 'Current conversion (%)', 2, true),
      field('target', 'Target conversion (%)', 3, true),
      field('aov', 'Net order value (USD)', 75)
    ],
    formula:
      'Additional orders = visits × (target conversion − current conversion). Additional revenue = additional orders × net order value. Traffic and order value are held constant.',
    calculate: (v) => [
      ['Additional orders', (v.visits * (v.target - v.current)) / 100, 'count'],
      [
        'Additional revenue',
        ((v.visits * (v.target - v.current)) / 100) * v.aov
      ],
      [
        'Relative conversion change',
        v.current > 0
          ? ((v.target - v.current) / v.current) * 100
          : 'Undefined when current conversion is zero',
        'percent'
      ]
    ]
  },
  budget: {
    fields: [
      field('budget', 'Monthly program budget (USD)', 5000),
      field('fixed', 'Fixed program costs (USD)', 500),
      field('gifts', 'Gifts and creator fees (USD)', 1000),
      field('rate', 'Commission (%)', 15, true)
    ],
    formula:
      'Commission capacity = budget − fixed costs − gifts and creator fees. Supported sales = commission capacity ÷ commission rate. This is a spending limit, not guaranteed revenue.',
    calculate: (v) => {
      const remaining = v.budget - v.fixed - v.gifts;
      return [
        ['Available for commissions', remaining],
        [
          'Sales supported by commission budget',
          remaining < 0
            ? 'Costs exceed budget'
            : v.rate > 0
            ? remaining / (v.rate / 100)
            : 'No commission-based sales limit at 0%'
        ]
      ];
    }
  }
};
function calculateTool(type, values) {
  const definition = calculatorDefinitions[type];
  if (!definition) return { error: 'Unknown calculator.' };
  const parsed = {};
  for (const f of definition.fields) {
    const raw = values[f.key];
    const n = Number(raw);
    if (
      raw === undefined ||
      raw === null ||
      String(raw).trim() === '' ||
      !Number.isFinite(n) ||
      n < 0 ||
      (f.percent && n > 100) ||
      (f.integer && !Number.isSafeInteger(n))
    )
      return {
        error: `Enter ${f.label.toLowerCase()} ${
          f.percent
            ? 'between 0 and 100'
            : f.integer
            ? 'as a non-negative whole number (up to 9,007,199,254,740,991)'
            : 'as a non-negative number'
        }.`
      };
    parsed[f.key] = n;
  }
  const error = definition.validate?.(parsed);
  if (error) return { error };
  const results = definition.calculate(parsed);
  if (
    results.some(
      ([, value]) => typeof value === 'number' && !Number.isFinite(value)
    )
  )
    return {
      error: 'These values are too large to calculate. Try smaller numbers.'
    };
  return { results };
}
function buildUtmUrl(url, source, medium, campaign) {
  try {
    const parsed = new URL(url);
    if (!['https:', 'http:'].includes(parsed.protocol))
      return { error: 'Use a complete http:// or https:// store URL.' };
    if (!source.trim() || !medium.trim() || !campaign.trim())
      return { error: 'Fill in source, medium and campaign.' };
    parsed.searchParams.set('utm_source', source.trim());
    parsed.searchParams.set('utm_medium', medium.trim());
    parsed.searchParams.set('utm_campaign', campaign.trim());
    return { url: parsed.toString() };
  } catch {
    return { error: 'Use a complete http:// or https:// store URL.' };
  }
}
module.exports = { calculatorDefinitions, calculateTool, buildUtmUrl };
